import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Layout } from "@/components/layout/Layout";
import SEO from "@/components/SEO.jsx";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  ArrowRight,
  Globe,
  Code,
  Smartphone,
  Zap,
  Shield,
  TrendingUp,
  CheckCircle,
  ChevronDown,
  Layers,
} from "lucide-react";

const capabilities = [
  { icon: Shield, title: "Regular website updates" },
  { icon: Layers, title: "WordPress website maintenance in Vancouver" },
  { icon: Code, title: "Plugin and theme updates" },
  { icon: Shield, title: "Website security services" },
  { icon: Zap, title: "Malware monitoring" },
  { icon: Layers, title: "Scheduled backups" },
  { icon: TrendingUp, title: "Performance optimization" },
  { icon: Smartphone, title: "Emergency website repair in Vancouver" },
  { icon: Code, title: "E-commerce website maintenance support" },
];


const processSteps = [
  { number: 1, title: "Monthly maintenance plan" },
  { number: 2, title: "Security monitoring" },
  { number: 3, title: "Website backups" },
  { number: 4, title: "Performance checks" },
  { number: 5, title: "Technical issue resolution" },
  { number: 6, title: "Clear maintenance reports" },
  { number: 7, title: "Ongoing website support services" },
];

const faqs = [
  {
    question: "How often should website maintenance be done?",
    answer:
      "Websites should be monitored regularly. Updates and security checks are typically performed monthly or as needed.",
  },
  {
    question: "Do you provide WordPress website maintenance in Vancouver?",
    answer:
      "Yes. We provide WordPress website maintenance services including updates and security monitoring.",
  },
  {
    question: "What happens if my website goes down?",
    answer:
      "We provide emergency website repair services to resolve urgent technical issues.",
  },
  {
    question: "Do you maintain e-commerce websites?",
    answer:
      "Yes. We offer ecommerce website maintenance in Vancouver, including performance and security monitoring.",
  },
  {
    question: "Is website maintenance necessary for small businesses?",
    answer:
      "Yes. Regular updates help prevent security issues and maintain performance.",
  },
];

const whyChooseUs = [
  {
    title: "Proactive Monitoring",
    description:
      "We monitor your website regularly to prevent issues before they affect your business.",
  },
  {
    title: "Security & Backup Management",
    description:
      "We implement website security services and scheduled backups.",
  },
  {
    title: "Technical Support",
    description:
      "Quick response for website issues or emergency website repair needs.",
  },
];

export default function WebsiteDevelopmentPage() {
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <Layout>
      {/* SEO Meta */}
<SEO
  title="Website Maintenance Services Vancouver | Square Trigger"
  description="Square Trigger provides website maintenance services in Vancouver including WordPress support, security monitoring, backups, and emergency website repair."
  ogTitle="Website Maintenance Services Vancouver | Square Trigger"
  ogDescription="Square Trigger provides website maintenance services in Vancouver including WordPress support, security monitoring, backups, and emergency website repair."
/>



   {/* Hero Section */}
<section className="pt-24 sm:pt-28 lg:pt-32 pb-16 sm:pb-18 lg:pb-20 relative overflow-hidden">
  <div className="absolute inset-0 bg-gradient-to-br from-foreground via-foreground/95 to-foreground/90" />
  <div className="absolute inset-0">
    <div className="absolute top-16 sm:top-20 right-6 sm:right-20 w-72 sm:w-96 h-72 sm:h-96 bg-primary/20 rounded-full blur-3xl" />
    <div className="absolute bottom-16 sm:bottom-20 left-6 sm:left-20 w-56 sm:w-72 h-56 sm:h-72 bg-secondary/20 rounded-full blur-3xl" />
  </div>

  <div className="container-custom relative z-10">
    <div className="max-w-4xl mx-auto text-center">
      <motion.h1
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.1 }}
        className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold text-background mb-5 sm:mb-6 leading-tight"
      >
        Website Maintenance Services in{" "}
        <span className="gradient-text">Vancouver</span>
      </motion.h1>

      <motion.p
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="text-lg sm:text-xl text-background/70 mb-8 max-w-2xl mx-auto leading-relaxed"
      >
        Square Trigger provides reliable website maintenance and support services in Vancouver to keep your website secure, updated, and running smoothly.
      </motion.p>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.3 }}
        className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-center justify-center"
      >
        {/* Primary CTA */}
        <Button
          variant="gradient"
          size="lg"
          asChild
          className="group w-full sm:w-auto justify-center"
        >
          <Link to="/get-started" className="flex items-center justify-center gap-2">
            Request Maintenance Plan
            <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
          </Link>
        </Button>

        {/* Secondary CTA */}
        <Button
          variant="glass"
          size="lg"
          asChild
          className="w-full sm:w-auto justify-center text-white bg-white/10 border border-white/20 hover:shadow-[0_8px_30px_rgba(255,255,255,0.12)] hover:-translate-y-[1px] transition-all duration-300"
        >
          <Link to="/support">Get Support</Link>
        </Button>
      </motion.div>
    </div>
  </div>
</section>


    {/* Value Statement */}
<section className="pt-12 sm:pt-14 lg:pt-20 pb-6 sm:pb-8 lg:pb-10 bg-background relative overflow-hidden">
  <div className="absolute inset-0 pointer-events-none">
    <div className="absolute -top-24 left-10 w-72 h-72 bg-primary/5 rounded-full blur-3xl" />
    <div className="absolute -bottom-24 right-10 w-72 h-72 bg-secondary/5 rounded-full blur-3xl" />
  </div>

  <div className="container-custom relative z-10">
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="max-w-5xl mx-auto text-center"
    >
      <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mb-4">
        Ongoing Website Support{" "}
        <span className="gradient-text">You Can Rely On</span>
      </h2>

      <p className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-3xl mx-auto mb-4">
        At <strong>Square Trigger</strong>, we provide website maintenance services in Vancouver for businesses that need stability and long-term performance.
      </p>

      <p className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-3xl mx-auto mb-4">
        Websites require regular updates, security checks, backups, and monitoring. Without proper maintenance, performance can drop and security risks increase.
      </p>

      <p className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-3xl mx-auto">
        We ensure your website stays updated, secure, and fully functional.
      </p>
    </motion.div>
  </div>
</section>


    {/* Why Choose Us */}
<section className="pt-12 sm:pt-14 lg:pt-14 pb-6 sm:pb-8 lg:pb-10 bg-background relative overflow-hidden">
  <div className="container-custom">
    {/* Section Heading */}
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="text-center max-w-3xl mx-auto mb-12"
    >
      <p className="text-sm font-semibold tracking-wide text-primary uppercase mb-2">
        WHY CHOOSE US
      </p>

      <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground">
        Reliable Website Support Services
      </h2>
    </motion.div>

    {/* Main Content Grid */}
    <div className="grid lg:grid-cols-2 gap-10 items-start">
      {/* Right Side Points */}
      <div className="space-y-6">
        {whyChooseUs.map((item, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.08 }}
            className="p-6 bg-card rounded-2xl border border-border shadow-soft hover:shadow-medium transition-shadow"
          >
            <h3 className="font-semibold text-foreground mb-2">{item.title}</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {item.description}
            </p>
          </motion.div>
        ))}
      </div>

      {/* Trust Block */}
      <div className="space-y-6">
        <div className="p-6 bg-card rounded-2xl border border-border shadow-soft">
          <h3 className="font-semibold text-foreground mb-2">
            100% Website Stability Focus
          </h3>
          <p className="text-sm text-muted-foreground leading-relaxed">
            We prioritize uptime, performance, and reliability.
          </p>
        </div>

        <div className="p-6 bg-card rounded-2xl border border-border shadow-soft">
          <h3 className="font-semibold text-foreground mb-2">
            Clear Communication
          </h3>
          <p className="text-sm text-muted-foreground leading-relaxed">
            You receive updates and clear explanations of work completed.
          </p>
        </div>
      </div>
    </div>
  </div>
</section>


    {/* Website Maintenance Services  */}
<section className="pt-12 sm:pt-14 lg:pt-16 pb-6 sm:pb-8 lg:pb-10 bg-background relative overflow-hidden">
  <div className="container-custom">
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="text-center max-w-3xl mx-auto mb-12 sm:mb-14"
    >
      {/* Eyebrow */}
      <p className="text-sm font-semibold tracking-wide text-primary uppercase mb-2">
        WHAT WE OFFER
      </p>

      <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground">
        Our Website Maintenance Services
      </h2>

      <p className="text-base sm:text-lg text-muted-foreground mt-4 leading-relaxed">
        We provide structured website maintenance and support services, including:
      </p>
    </motion.div>

    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
      {capabilities.map((capability, index) => {
        const Icon = capability.icon;
        return (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: index * 0.07 }}
            className="h-full"
          >
            <Card className="p-6 h-full border-border hover:border-primary/30 transition-all duration-300 card-hover">
              <div className="w-12 h-12 rounded-xl gradient-primary flex items-center justify-center mb-4">
                <Icon className="h-6 w-6 text-primary-foreground" />
              </div>

              <h3 className="text-lg sm:text-xl font-display font-semibold text-foreground">
                {capability.title}
              </h3>
            </Card>
          </motion.div>
        );
      })}
    </div>
  </div>
</section>

{/* What You Receive */}
<section className="pt-12 sm:pt-14 lg:pt-12 pb-6 sm:pb-8 lg:pb-10 bg-background relative overflow-hidden">
  <div className="container-custom">
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="text-center max-w-3xl mx-auto mb-12 sm:mb-16"
    >
      <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground">
        What You <span className="gradient-text">Receive</span>
      </h2>

      <p className="text-base sm:text-lg text-muted-foreground mt-4 leading-relaxed">
        When you work with Square Trigger, you receive:
      </p>
    </motion.div>

    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
      {processSteps.map((step, index) => (
        <motion.div
          key={index}
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: index * 0.08 }}
          className="relative h-full"
        >
          <Card className="p-6 h-full border-border hover:border-primary/30 transition-colors card-hover">
            <div className="flex items-center justify-between mb-4">
              <div className="text-3xl font-display font-bold gradient-text">
                {step.number}
              </div>
              <div className="w-10 h-10 rounded-xl gradient-primary-soft flex items-center justify-center">
                <div className="w-2.5 h-2.5 rounded-full bg-primary" />
              </div>
            </div>

            <h3 className="text-lg sm:text-xl font-display font-semibold text-foreground">
              {step.title}
            </h3>
          </Card>
        </motion.div>
      ))}
    </div>
  </div>
</section>
{/* How Our Maintenance Process Works */}
<section className="pt-12 sm:pt-14 lg:pt-16 pb-6 sm:pb-8 lg:pb-10 bg-background relative overflow-hidden">
  <div className="absolute inset-0 pointer-events-none">
    <div className="absolute -top-24 right-10 w-72 h-72 bg-primary/5 rounded-full blur-3xl" />
    <div className="absolute -bottom-24 left-10 w-72 h-72 bg-secondary/5 rounded-full blur-3xl" />
  </div>

  <div className="container-custom relative z-10">
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="text-center max-w-3xl mx-auto mb-10 sm:mb-12"
    >
      <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground">
        How Our <span className="gradient-text">Maintenance Process Works</span>
      </h2>
    </motion.div>

    <div className="grid sm:grid-cols-2 gap-6 lg:gap-8">
      {[
        {
          number: "01",
          title: "Website Review",
          description: "We review your current website condition and technical setup.",
        },
        {
          number: "02",
          title: "Maintenance Setup",
          description: "We configure monitoring, backups, and update systems.",
        },
        {
          number: "03",
          title: "Regular Updates & Monitoring",
          description: "We perform scheduled updates and security checks.",
        },
        {
          number: "04",
          title: "Issue Resolution & Reporting",
          description: "We resolve problems and provide summary reports.",
        },
      ].map((step, index) => (
        <motion.div
          key={index}
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: index * 0.07 }}
          className="h-full"
        >
          <Card className="p-6 h-full border-border hover:border-primary/30 transition-all duration-300 card-hover">
            <div className="flex items-center justify-between mb-4">
              <div className="text-3xl font-display font-bold gradient-text">
                {step.number}
              </div>
              <div className="w-10 h-10 rounded-xl gradient-primary-soft flex items-center justify-center">
                <div className="w-2.5 h-2.5 rounded-full bg-primary" />
              </div>
            </div>

            <h3 className="text-lg sm:text-xl font-display font-semibold text-foreground mb-2">
              {step.title}
            </h3>

            <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
              {step.description}
            </p>
          </Card>
        </motion.div>
      ))}
    </div>
  </div>
</section>
      

      {/* FAQs */}
<section className="pt-12 sm:pt-14 lg:pt-16 pb-6 sm:pb-8 lg:pb-10 bg-background relative overflow-hidden">
  <div className="container-custom">
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="text-center max-w-3xl mx-auto mb-10 sm:mb-12"
    >
     <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mt-3">
  Website Maintenance – <span className="gradient-text">FAQs</span>
</h2>

    </motion.div>

    <div className="max-w-3xl mx-auto space-y-4">
      {faqs.map((faq, index) => (
        <motion.div
          key={index}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: index * 0.05 }}
          className="bg-card rounded-xl border border-border overflow-hidden"
        >
          <button
            onClick={() => setOpenFaq(openFaq === index ? null : index)}
            className="w-full flex items-center justify-between p-5 sm:p-6 text-left hover:bg-accent/50 transition-colors"
          >
            <span className="font-semibold text-foreground pr-6">
              {faq.question}
            </span>
            <ChevronDown
              className={`h-5 w-5 text-muted-foreground transition-transform duration-300 flex-shrink-0 ${
                openFaq === index ? "rotate-180" : ""
              }`}
            />
          </button>

          <AnimatePresence>
            {openFaq === index && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="overflow-hidden"
              >
                <div className="px-5 sm:px-6 pb-5 sm:pb-6 text-muted-foreground leading-relaxed">
                  {faq.answer}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      ))}
    </div>

    {/* FAQ Schema */}
    <script type="application/ld+json">
      {JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((faq) => ({
          "@type": "Question",
          name: faq.question,
          acceptedAnswer: {
            "@type": "Answer",
            text: faq.answer,
          },
        })),
      })}
    </script>
  </div>
</section>

    {/*  CTA  */}
<section className="pt-12 sm:pt-14 lg:pt-16 pb-6 sm:pb-8 lg:pb-10 bg-background relative overflow-hidden">
  <div className="container-custom">
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="text-center max-w-3xl mx-auto"
    >
      <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mb-4 sm:mb-6">
        Keep Your Website Running{" "}
        <span className="gradient-text">Smoothly</span>
      </h2>

      <p className="text-base sm:text-lg text-muted-foreground mb-8 leading-relaxed max-w-2xl mx-auto">
        Let’s create a maintenance plan that protects your website and supports long-term performance.
      </p>

      <div className="flex justify-center">
        <Button
          variant="gradient"
          size="lg"
          asChild
          className="group w-full sm:w-auto justify-center"
        >
          <Link to="/get-started" className="flex items-center justify-center gap-2">
            Start Maintenance Plan
            <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
          </Link>
        </Button>
      </div>
    </motion.div>
  </div>
</section>

    </Layout>
  );
}